/**
 * Google `MapView` adapter — wraps `@vis.gl/react-google-maps`. Mounts an
 * `APIProvider` at the top of the tree (lazy: only once per app via a
 * module-level flag) and renders a `Map` against it.
 *
 * Pin-drop is uniformly **left-click** — matches the locked decision in #321.
 * Clicking a labeled POI icon counts as a pin drop too: `clickableIcons` is on,
 * the native InfoWindow is suppressed (`e.stop()`), and the POI's `placeId` is
 * forwarded on the `MapMouseEvent` so the consumer can resolve its real name +
 * address via `getPlaceDetails`. (This re-enables the POI-select behavior that
 * #321 had deferred — now wired through the connector instead of Google's UI.)
 *
 * Per the architect's Phase 2 brief:
 *  - Builds must not hard-fail when the Google API key is missing. We render
 *    an inline notice and short-circuit — the host page stays interactive.
 *  - Map ID is required for AdvancedMarker support; we read it but don't
 *    require it. Markers fall back to legacy pins when absent.
 *
 * Map style toggle is implemented via the `mapTypeId` prop ('roadmap' vs
 * 'hybrid') to mirror Mapbox streets / satellite. We use 'hybrid' over
 * 'satellite' so road labels remain visible (the Mapbox satellite-streets
 * style has the same labels-over-imagery feel).
 */

import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  createContext,
  useContext,
  useMemo,
} from 'react';
import type * as React from 'react';
import {
  APIProvider,
  Map as GoogleMap,
  useMap as useGoogleMap,
} from '@vis.gl/react-google-maps';
import type { MapViewProps, MapHandle } from '../../types';
import { getGoogleMapsApiKey, getGoogleMapsMapId } from './token';

const STYLE_TYPE: Record<NonNullable<MapViewProps['style']>, 'roadmap' | 'hybrid'> = {
  streets: 'roadmap',
  satellite: 'hybrid',
};

/**
 * Stable id used for the map instance — must be unique across mounted maps
 * within the same APIProvider context. A counter is fine because we tear
 * down the map on unmount and Google's internal `mapInstances` map cleans up.
 */
let mapIdCounter = 0;
function nextMapId(): string {
  mapIdCounter += 1;
  return `twm-map-${mapIdCounter}`;
}

// Smooth, eased camera fly — Google has no Mapbox-style `flyTo`, so on vector
// (WebGL) maps we interpolate center + zoom per animation frame via
// `moveCamera`. Raster maps fall back to an instant jump (fractional zoom isn't
// supported there, so a stepped animation just looks choppy).
const FLY_DURATION_MS = 2500;
const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// Web Mercator world coords (world size 256 = "screen px at zoom 0", so 1 world
// unit == 2^zoom screen px). Used to drive the fly path in a space where the
// viewport width is comparable across zoom levels.
const MERC_WORLD = 256;
function mercatorXY(lat: number, lng: number): [number, number] {
  const latRad = (lat * Math.PI) / 180;
  const x = ((lng + 180) / 360) * MERC_WORLD;
  const y = ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * MERC_WORLD;
  return [x, y];
}
function unmercator(x: number, y: number): { lat: number; lng: number } {
  const lng = (x / MERC_WORLD) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / MERC_WORLD)));
  return { lat: (latRad * 180) / Math.PI, lng };
}

// Van Wijk & Nuij "smooth and efficient zooming and panning" — the same path
// d3.interpolateZoom / Mapbox flyTo use. Interpolates center + viewport-width
// (zoom) together so screen-space motion stays even: the camera pans while
// pulling back, then settles in, instead of zooming first and snapping over.
// Points are [worldX, worldY, viewportWidthInWorldUnits].
function smoothZoomPath(
  p0: [number, number, number],
  p1: [number, number, number],
): (t: number) => [number, number, number] {
  const rho = Math.SQRT2;
  const rho2 = rho * rho;
  const rho4 = rho2 * rho2;
  const [ux0, uy0, w0] = p0;
  const [ux1, uy1, w1] = p1;
  const dx = ux1 - ux0;
  const dy = uy1 - uy0;
  const d2 = dx * dx + dy * dy;
  // Degenerate (no pan): pure logarithmic zoom interpolation.
  if (d2 < 1e-10) {
    const S = Math.log(w1 / w0) / rho;
    return (t) => [ux0 + t * dx, uy0 + t * dy, w0 * Math.exp(rho * t * S)];
  }
  const d1 = Math.sqrt(d2);
  const b0 = (w1 * w1 - w0 * w0 + rho4 * d2) / (2 * w0 * rho2 * d1);
  const b1 = (w1 * w1 - w0 * w0 - rho4 * d2) / (2 * w1 * rho2 * d1);
  const r0 = Math.log(Math.sqrt(b0 * b0 + 1) - b0);
  const r1 = Math.log(Math.sqrt(b1 * b1 + 1) - b1);
  const S = (r1 - r0) / rho;
  return (t) => {
    const s = t * S;
    const coshr0 = Math.cosh(r0);
    const u = (w0 / (rho2 * d1)) * (coshr0 * Math.tanh(rho * s + r0) - Math.sinh(r0));
    return [ux0 + u * dx, uy0 + u * dy, (w0 * coshr0) / Math.cosh(rho * s + r0)];
  };
}

/**
 * Per-MapView context — exposes the active `google.maps.Map` to descendant
 * markers so they can render against the same map instance.
 */
interface GoogleMapContextValue {
  mapId: string;
  ready: boolean;
}
const TwmGoogleMapContext = createContext<GoogleMapContextValue | null>(null);

export function useTwmGoogleMap(): GoogleMapContextValue | null {
  return useContext(TwmGoogleMapContext);
}

/**
 * Bridges the imperative `MapHandle` to the underlying `google.maps.Map` once
 * the API has loaded. Rendered inside the `Map` so `useMap` returns the right
 * instance.
 */
const MapHandleBridge: React.FC<{
  mapRef?: React.Ref<MapHandle>;
  onMoveOrZoom?: () => void;
  onLoad?: () => void;
  onReady: () => void;
}> = ({ mapRef, onMoveOrZoom, onLoad, onReady }) => {
  const map = useGoogleMap();
  const onMoveOrZoomRef = useRef(onMoveOrZoom);
  const onLoadRef = useRef(onLoad);
  // Handle for the in-flight flyTo rAF loop so a new fly (or unmount) cancels it.
  const flyFrameRef = useRef<number | null>(null);
  useEffect(() => { onMoveOrZoomRef.current = onMoveOrZoom; }, [onMoveOrZoom]);
  useEffect(() => { onLoadRef.current = onLoad; }, [onLoad]);
  useEffect(() => () => {
    if (flyFrameRef.current != null) cancelAnimationFrame(flyFrameRef.current);
  }, []);

  useImperativeHandle(
    mapRef,
    (): MapHandle => ({
      projectLatLng: (ll) => {
        if (!map) return null;
        const projection = map.getProjection();
        const overlay = (map as unknown as { __overlay?: google.maps.OverlayView }).__overlay;
        // Google's `Projection` only converts to world coords. To get pixel
        // coords within the container, we use an OverlayView. Build it lazily
        // and cache on the map instance so repeated calls don't reattach.
        if (!projection) return null;
        let ov = overlay;
        if (!ov) {
          ov = new google.maps.OverlayView();
          ov.onAdd = () => {};
          ov.draw = () => {};
          ov.onRemove = () => {};
          ov.setMap(map);
          (map as unknown as { __overlay?: google.maps.OverlayView }).__overlay = ov;
        }
        const proj = ov.getProjection();
        if (!proj) return null;
        const px = proj.fromLatLngToContainerPixel(new google.maps.LatLng(ll.lat, ll.lng));
        if (!px) return null;
        return { x: px.x, y: px.y };
      },
      flyTo: (ll, zoom) => {
        if (!map) return;
        const targetZoom = zoom !== undefined ? zoom : Math.max(map.getZoom() ?? 0, 19);
        const startCenter = map.getCenter();
        const startZoom = map.getZoom();

        // Only ease on vector maps — moveCamera with fractional zoom is smooth
        // there. Anything else snaps (matches the prior behavior).
        const isVector =
          !!google.maps.RenderingType &&
          (map as unknown as { getRenderingType?: () => unknown }).getRenderingType?.() ===
            google.maps.RenderingType.VECTOR;
        const canEase =
          isVector &&
          typeof (map as unknown as { moveCamera?: unknown }).moveCamera === 'function' &&
          !!startCenter &&
          startZoom != null;

        if (!canEase) {
          map.panTo({ lat: ll.lat, lng: ll.lng });
          map.setZoom(targetZoom);
          return;
        }

        if (flyFrameRef.current != null) cancelAnimationFrame(flyFrameRef.current);
        const z0 = startZoom as number;
        // Viewport width in screen px == world units at zoom 0, scaled by 2^zoom.
        const div = (map as unknown as { getDiv?: () => HTMLElement }).getDiv?.();
        const vp = div?.clientWidth || div?.clientHeight || 512;
        const [sx, sy] = mercatorXY(startCenter!.lat(), startCenter!.lng());
        const [tx, ty] = mercatorXY(ll.lat, ll.lng);
        const path = smoothZoomPath(
          [sx, sy, vp / Math.pow(2, z0)],
          [tx, ty, vp / Math.pow(2, targetZoom)],
        );
        const t0 = performance.now();
        const step = (nowMs: number) => {
          const t = Math.min(1, (nowMs - t0) / FLY_DURATION_MS);
          const [x, y, w] = path(easeInOutCubic(t));
          map.moveCamera({ center: unmercator(x, y), zoom: Math.log2(vp / w) });
          flyFrameRef.current = t < 1 ? requestAnimationFrame(step) : null;
        };
        flyFrameRef.current = requestAnimationFrame(step);
      },
      fitBounds: (points, opts) => {
        if (!map || points.length === 0) return;
        if (points.length === 1) {
          map.panTo({ lat: points[0].lat, lng: points[0].lng });
          map.setZoom(opts?.maxZoom ?? 19);
          return;
        }
        const bounds = new google.maps.LatLngBounds();
        points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
        map.fitBounds(bounds, opts?.padding ?? 60);
        // Google's fitBounds doesn't accept maxZoom directly — clamp after.
        const max = opts?.maxZoom;
        if (max !== undefined) {
          // Wait one tick so fitBounds resolves before we clamp.
          window.setTimeout(() => {
            const z = map.getZoom();
            if (z !== undefined && z > max) map.setZoom(max);
          }, 0);
        }
      },
    }),
    [map],
  );

  useEffect(() => {
    if (!map) return;
    const listeners: google.maps.MapsEventListener[] = [];
    if (onMoveOrZoom) {
      listeners.push(map.addListener('center_changed', () => onMoveOrZoomRef.current?.()));
      listeners.push(map.addListener('zoom_changed', () => onMoveOrZoomRef.current?.()));
      listeners.push(map.addListener('bounds_changed', () => onMoveOrZoomRef.current?.()));
    }
    onLoadRef.current?.();
    onReady();
    return () => {
      listeners.forEach((l) => l.remove());
    };
  }, [map, onMoveOrZoom, onReady]);

  return null;
};

export const GoogleMapView: React.FC<MapViewProps> = ({
  center,
  zoom,
  style = 'streets',
  interactive = true,
  onMapClick,
  onMoveOrZoom,
  onLoad,
  mapRef,
  children,
  style_,
  className,
}) => {
  const apiKey = getGoogleMapsApiKey();
  const mapId = getGoogleMapsMapId();
  const internalMapIdRef = useRef<string>(nextMapId());
  const [ready, setReady] = useState(false);

  // Memoize context value so descendant Marker components don't re-render on
  // every parent render.
  const ctx = useMemo<GoogleMapContextValue>(
    () => ({ mapId: internalMapIdRef.current, ready }),
    [ready],
  );

  if (!apiKey) {
    // Fail-soft: render placeholder so the page stays interactive. Logged
    // once so debugging is loud.
    if (typeof window !== 'undefined') {
      console.error(
        '[maps:google] Missing API key — set VITE_GOOGLE_MAPS_API_KEY (admin) or NEXT_PUBLIC_GOOGLE_MAPS_API_KEY (website).',
      );
    }
    return (
      <div
        className={className}
        style={{ ...style_, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <span style={{ fontSize: 12, color: '#666' }}>
          Map unavailable — Google Maps API key not configured.
        </span>
      </div>
    );
  }

  return (
    <APIProvider apiKey={apiKey} libraries={['marker', 'places', 'geocoding']}>
      <TwmGoogleMapContext.Provider value={ctx}>
        <GoogleMap
          id={internalMapIdRef.current}
          mapId={mapId}
          defaultCenter={{ lat: center.lat, lng: center.lng }}
          defaultZoom={zoom}
          mapTypeId={STYLE_TYPE[style]}
          gestureHandling={interactive ? 'auto' : 'none'}
          disableDefaultUI={!interactive}
          // On interactive maps, suppress the native controls that duplicate or
          // collide with the LocationPicker's overlay chrome: Map-Type (we render
          // our own Streets/Satellite toggle), Street View + Fullscreen (land
          // under our top-left search / top-right controls and aren't useful for
          // picking a spot), and the vector camera/tilt widget. We keep Google's
          // native +/- zoom (bottom-right — no collision, and users expect it).
          // Non-interactive maps already drop everything via disableDefaultUI.
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          cameraControl={false}
          // POI icons are clickable so a user can pick a business / landmark
          // directly. We suppress Google's default InfoWindow below and surface
          // the placeId on the event instead.
          clickableIcons
          style={style_}
          className={className}
          onClick={(e) => {
            const ll = e.detail.latLng;
            if (!ll) return;
            const placeId = e.detail.placeId ?? undefined;
            // A POI-icon click is "stoppable" — stop it so Google doesn't pop
            // its own InfoWindow over our pin + floating popover.
            if (placeId && e.stoppable) e.stop();
            onMapClick?.({
              lngLat: { lat: ll.lat, lng: ll.lng },
              originalEvent: e.domEvent as unknown as MouseEvent,
              placeId,
            });
          }}
        >
          <MapHandleBridge
            mapRef={mapRef}
            onMoveOrZoom={onMoveOrZoom}
            onLoad={onLoad}
            onReady={() => setReady(true)}
          />
          {children}
        </GoogleMap>
      </TwmGoogleMapContext.Provider>
    </APIProvider>
  );
};
